import type { PublicResultsResponse, SupportOverTimeResponse } from '../shared/api';
import { api, escapeHtml } from './http';
import { pageLayout } from './layout';
import {
  createResultViewState,
  installResultOptionMenus,
  renderActiveModifiers,
  renderResultQuestionCard,
  type ResultViewState,
} from './results-page';
import { installSupportTimeChart, renderSupportTimeChartCard } from './support-over-time-page';

export async function renderLandingPage(root: HTMLElement): Promise<void> {
  root.innerHTML = pageLayout('Survey Results', '<div id="publicResults"><div class="loading-state"><span class="spinner"></span>Loading results…</div></div>', {
    publicNav: true,
    wide: true,
    showAbout: false,
    showHeading: false,
  });
  const content = root.querySelector<HTMLElement>('#publicResults')!;
  await loadPublicResults(content, createResultViewState());
}

async function loadPublicResults(content: HTMLElement, viewState: ResultViewState): Promise<void> {
  try {
    const parameters = new URLSearchParams();
    viewState.facets.forEach(({ questionId, option }) => parameters.append('facet', `${questionId}:${option}`));
    viewState.exclusions.forEach(({ questionId, option }) => parameters.append('exclude', `${questionId}:${option}`));
    const [results, allSurveyResults] = await Promise.all([
      api<PublicResultsResponse>(`/api/results${parameters.size ? `?${parameters}` : ''}`),
      api<SupportOverTimeResponse>('/api/support-over-time?weighting=raw&includeBias=true'),
    ]);
    content.innerHTML = `<section class="public-results-intro">
      <div class="public-survey-title">
        <h1>Petrovitsky Park Open Space</h1>
        <p>Community Survey</p>
      </div>
      <h2>This survey closed at 5:11 pm PDT Aug 25th, 2026</h2>
      <p class="public-thank-you">Thank you to everyone that took the time to give your input!</p>
      <img class="public-pin-map" src="/pin-map.png" alt="Map of survey visit pins">
      <a class="primary-button public-map-link" href="/results-map">View neighborhood results map</a>
    </section>
    <section class="public-results-summary" aria-label="Survey response totals">
      <div class="summary-stat"><strong>${results.responseCount}</strong><span>Responses</span></div>
      <div class="summary-stat"><strong>${results.householdCount}</strong><span>Households</span></div>
    </section>
    ${renderActiveModifiers(viewState)}
    <div class="results-grid">${results.questions.map((question, index) => renderResultQuestionCard(question, index, viewState)).join('')}${renderSupportTimeChartCard(allSurveyResults, { embedded: true })}</div>
    <p class="public-reference-lead"><strong>This is the only survey of the surrounding neighborhoods that incorporates the actual design of the course</strong></p>
    <nav class="card public-reference-links" aria-label="Related 2024 references">
      <p class="public-reference-intro">Community Partnerships and Grants conducted a survey in Fall 2024 <em>after</em> signing the contract with NWDGA to justify moving forward with the project.</p>
      <a href="https://publicinput.com/Report/grvscurl2mj" target="_blank" rel="noopener noreferrer">Compare to the Community Partnerships and Grants 2024 Survey</a>
      <a href="/Petro-DG-Results-1-Pager.pdf" target="_blank" rel="noopener noreferrer">Official 2024 survey results flier</a>
      <img class="public-strong-support-image" src="/strong-support.png" alt="Strong support result from the official 2024 survey" loading="lazy">
      <div class="public-reference-image-grid" aria-label="NWDGA survey coordination images">
        ${renderReferenceImage('/disc-golf-ad.png', 'Disc golf voting advertisement')}
        ${renderReferenceImage('/nwdga-account.png', 'NWDGA account post')}
        ${renderReferenceImage('/nwdga-facebook.png', 'NWDGA Facebook post')}
      </div>
      <p class="public-reference-coordination"><strong>NWDGA coordinated with others to vote in that survey</strong></p>
      <div class="public-comment-image-grid" aria-label="Disc golf survey comments">
        ${renderCommentImage('/dg-comments-1.png', 'Disc golf survey comments 1')}
        ${renderCommentImage('/dg-comments-2.png', 'Disc golf survey comments 2')}
        ${renderCommentImage('/dg-comments-3.png', 'Disc golf survey comments 3')}
        ${renderCommentImage('/dg-comments-4.png', 'Disc golf survey comments 4')}
        ${renderCommentImage('/dg-comments-5.png', 'Disc golf survey comments 5')}
      </div>
      <p class="public-reference-closing">That really is how far you have to scroll to find the first negative comment. This is because NWDGA already organized and planned on swarming the survey before it came out. In contrast, local community members were caught by surprise. Most were unaware of the project even after the survey finished.</p>
      <div class="public-nearby-assessment">
        <p>This is how that survey assessed if you lived nearby. It also used IP address which is often not accurate and can have false negatives.</p>
        <img src="/how-live-nearby.png" alt="How the 2024 survey assessed whether a respondent lived nearby" loading="lazy">
        <div class="public-nearby-comparison">
          <p>668 attested to living 5 minutes from Petrovitsky Park, but only 239 actually picked a zip code where that could be true.</p>
        </div>
      </div>
    </nav>
    <p class="public-current-survey-count">Meanwhile, the Petrovitsky Park Open Space &amp; Trail Community Survey has <strong>${allSurveyResults.totalResponseCount}</strong> responses across all three variants.</p>
    <dialog id="publicReferenceImageDialog" class="public-reference-image-dialog" aria-label="Expanded image">
      <button class="public-reference-image-close" type="button" aria-label="Close expanded image">×</button>
      <img src="" alt="">
    </dialog>`;
    installResultOptionMenus(content, viewState, () => loadPublicResults(content, viewState));
    installSupportTimeChart(content, allSurveyResults);
    installReferenceImageDialog(content);
  } catch (error) {
    content.innerHTML = `<div class="error-state">${escapeHtml(error instanceof Error ? error.message : 'Results could not be loaded.')}</div>`;
  }
}

function renderReferenceImage(src: string, alt: string): string {
  return `<button class="public-reference-image-button" type="button" data-reference-image="${src}" data-reference-alt="${alt}" aria-label="View ${alt.toLowerCase()} larger"><img src="${src}" alt="${alt}" loading="lazy"></button>`;
}

function renderCommentImage(src: string, alt: string): string {
  return `<img src="${src}" alt="${alt}" loading="lazy">`;
}

function installReferenceImageDialog(content: HTMLElement): void {
  const dialog = content.querySelector<HTMLDialogElement>('#publicReferenceImageDialog');
  const expandedImage = dialog?.querySelector<HTMLImageElement>('img');
  if (!dialog || !expandedImage) return;
  content.querySelectorAll<HTMLButtonElement>('[data-reference-image]').forEach((button) => {
    button.addEventListener('click', () => {
      expandedImage.src = button.dataset.referenceImage!;
      expandedImage.alt = button.dataset.referenceAlt!;
      dialog.showModal();
    });
  });
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target === expandedImage || (event.target as Element).closest('.public-reference-image-close')) dialog.close();
  });
}
